/** Shared verbatim with CIT.Umbrella.UI/src/helpers/link-tracking.ts. */
export type TrackingProvider =
	| "plausible"
	| "gtag"
	| "gaq"
	| "clarity"
	| "mixpanel";
export type TrackingProperties = Readonly<Record<string, string>>;
export type TrackingConfig = {
	providers?: readonly TrackingProvider[];
	isAllowed?: (provider: TrackingProvider) => boolean;
	/** Defaults to "Language picker"; use a test goal name or false to disable. */
	languageEvent?: string | false;
};
type TrackingWindow = Window & {
	umbrellaTracking?: TrackingConfig;
	plausible?: (name: string, options?: { props: TrackingProperties }) => void;
	gtag?: (command: string, name: string, props?: TrackingProperties) => void;
	_gaq?: { push(value: string[]): unknown };
	clarity?: (command: string, name: string) => void;
	mixpanel?: { track?: (name: string, props?: TrackingProperties) => void };
};
type Registration = { references: number; dispose: () => void };
const registrationKey = Symbol.for("umbrella.linkTracking");

function gtagName(name: string): string {
	const normalized = name.replace(/[^\p{L}\p{N}_]/gu, "_");
	return Array.from(
		/^\p{L}/u.test(normalized) ? normalized : `event_${normalized}`,
	)
		.slice(0, 40)
		.join("");
}

/** Shared local event and consent-aware adapters for links and explicit UI actions. */
export function trackEvent(
	doc: Document,
	eventName: string,
	props?: TrackingProperties,
): void {
	const view = doc.defaultView;
	const name = eventName.trim();
	if (!view || !name) return;
	const analytics = view as TrackingWindow;
	const properties =
		props && Object.keys(props).length ? { ...props } : undefined;
	const detail = properties ? { name, props: { ...properties } } : { name };
	view.dispatchEvent(
		new view.CustomEvent("umbrella:tracking-event", { detail }),
	);
	const config = analytics.umbrellaTracking;
	const label =
		properties?.Language ??
		(properties ? JSON.stringify(properties) : undefined);
	for (const provider of new Set(config?.providers ?? [])) {
		try {
			if (config?.isAllowed && !config.isAllowed(provider)) continue;
			switch (provider) {
				case "plausible":
					if (typeof analytics.plausible === "function") {
						if (properties)
							analytics.plausible(name, { props: { ...properties } });
						else analytics.plausible(name);
					}
					break;
				case "gtag":
					if (typeof analytics.gtag === "function") {
						if (properties)
							analytics.gtag("event", gtagName(name), { ...properties });
						else analytics.gtag("event", gtagName(name));
					}
					break;
				case "gaq":
					if (typeof analytics._gaq?.push === "function")
						analytics._gaq.push(
							label
								? ["_trackEvent", "Link", name, label]
								: ["_trackEvent", "Link", name],
						);
					break;
				case "clarity":
					// Clarity events have no property argument; a session tag would
					// incorrectly associate later selections with earlier events.
					if (typeof analytics.clarity === "function")
						analytics.clarity("event", label ? `${name}: ${label}` : name);
					break;
				case "mixpanel":
					if (typeof analytics.mixpanel?.track === "function") {
						if (properties) analytics.mixpanel.track(name, { ...properties });
						else analytics.mixpanel.track(name);
					}
					break;
			}
		} catch {
			// Analytics failures must neither block actions nor other adapters.
		}
	}
}

/** Only call from a user's selection handler, never cookie/widget synchronization. */
export function trackLanguageSelection(
	doc: Document,
	code: string,
	label: string,
): void {
	const configured = (doc.defaultView as TrackingWindow | null)
		?.umbrellaTracking?.languageEvent;
	if (configured === false) return;
	const culture = code.trim();
	const language = label.trim() || culture;
	if (!language) return;
	const value =
		culture && !language.toLowerCase().endsWith(`(${culture.toLowerCase()})`)
			? `${language} (${culture})`
			: language;
	trackEvent(doc, configured ?? "Language picker", { Language: value });
}

/** One listener per document, including custom layouts, remounts and both renderers. */
export function initLinkTracking(doc: Document): () => void {
	const state = doc as Document & { [key: symbol]: Registration | undefined };
	let registration = state[registrationKey];
	if (registration) registration.references++;
	else {
		const view = doc.defaultView;
		if (!view) return () => {};
		const listener = (event: Event) => {
			const mouse = event as MouseEvent;
			if (
				(event.type === "click" && mouse.button !== 0) ||
				(event.type === "auxclick" && mouse.button !== 1)
			)
				return;
			const path = event.composedPath();
			if (
				path.some(
					(node) =>
						node instanceof view.Element &&
						node.matches(":disabled, [aria-disabled='true'], [inert]"),
				)
			)
				return;
			const element = path.find(
				(node) =>
					node instanceof view.Element &&
					(node.getAttribute("data-tracking-event")?.trim() ||
						node.getAttribute("data-tracking-language")?.trim()),
			) as Element | undefined;
			if (
				!element ||
				(event.type === "auxclick" && !element.closest("a[href]"))
			)
				return;
			const language = element.getAttribute("data-tracking-language")?.trim();
			if (language)
				trackLanguageSelection(
					doc,
					element.getAttribute("data-tracking-language-code") ?? "",
					language,
				);
			else trackEvent(doc, element.getAttribute("data-tracking-event") ?? "");
		};
		registration = {
			references: 1,
			dispose: () => {
				doc.removeEventListener("click", listener, true);
				doc.removeEventListener("auxclick", listener, true);
				delete state[registrationKey];
			},
		};
		state[registrationKey] = registration;
		doc.addEventListener("click", listener, true);
		doc.addEventListener("auxclick", listener, true);
	}
	const retained = registration;
	let released = false;
	return () => {
		if (released) return;
		released = true;
		if (--retained.references === 0) retained.dispose();
	};
}
